/**
 * Pure facts for the master config card: the eight sections of one config
 * as readable tiles (what each actually holds, on/off), who follows the
 * rules, who on that campaign does not yet, and which sections a follower
 * keeps as its own (named, never just counted).
 * For Media Devoted | Created By Ciro Autiero
 */
import { describeMix, DROP_GEN_POLICY_KINDS, DROP_GEN_SECTIONS, type DropGenMasterSummary, type DropGenSection, ordinaryMix, sameMix, stopLossOn } from '@trafficpulse/shared';

import type { DropGenContainerSummary, DropGenPlanDetail } from '../../../drop-generator.types';
import { STRATEGY_LABELS } from '../../editor/gateways/cards/strategy-copy';

export interface RuleTile {
  key: DropGenSection;
  label: string;
  value: string;
  on: boolean;
}

/** The section names the master config card shows, one place for every reader. */
export const SECTION_LABEL: Record<DropGenSection, string> = {
  gatewayStrategy: 'Gateway strategy',
  gatewayPool: 'Gateways & senders',
  creative: 'Texts',
  options: 'Send options',
  verdict: 'Verdicts',
  hourOptimizer: 'Hour optimizer',
  amount: 'Default amount',
  failMode: 'Mid-day fail',
  autoSend: 'Auto-send',
};

/** Phase 12 — the ranking's levers, only when they change something. */
function rankingWords(m: DropGenMasterSummary): string {
  const r = m.textRanking;
  if (!r) return '';
  // Phase 37 — the ordinary mix (proven + AI texts on each drop) and the kinds of drops with their own.
  const ordinary = ordinaryMix({ provenPerDrop: r.provenPerDrop, policy: r.policy, amount: r.aiPerDrop });
  const apart = DROP_GEN_POLICY_KINDS.filter((k) => r.policy?.[k] !== undefined && !sameMix(r.policy[k]!, ordinary));
  const bets = apart.length === 0 ? null : apart.every((k) => r.policy![k]!.proven > 0 && r.policy![k]!.ai === 0) ? `${apart.length} drop kinds proven only` : `${apart.length} drop kinds with a different mix`;
  const usual = ordinary.proven > 0 ? `${describeMix(ordinary)} texts/drop (top ${r.topN})` : null;
  const parts = [usual, bets, r.leanGroups ? 'leans on best groups' : null].filter(Boolean);
  return parts.length > 0 ? ` · ${parts.join(' · ')}` : '';
}

/** Only a non-default shortlink format is worth a word: "https + subdomain links". */
function shortlinkFormat(m: DropGenMasterSummary): string | null {
  const parts = [m.domainScheme === 'https' ? 'https' : null, m.clickIdStructure === 'subdomain' ? 'subdomain' : null].filter(Boolean);
  return parts.length > 0 ? `${parts.join(' + ')} links` : null;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Phase 20 — the group's auto-send: "on · cap 20,001 SMS · stop-loss set". */
function autoSendWords(m: DropGenMasterSummary): string {
  const a = m.autoSend;
  const cap = a.dailySmsCap === null ? 'no cap' : `cap ${a.dailySmsCap.toLocaleString('en-US')} SMS`;
  return `${a.autoSend ? 'on' : 'off — you send'} · ${cap} · ${stopLossOn(a) ? 'stop-loss on' : a.stopLossJson ? 'stop-loss off' : 'no stop-loss'}`;
}

export function ruleTiles(m: DropGenMasterSummary): RuleTile[] {
  const texts =
    m.textGroupCount > 0
      ? `${plural(m.textGroupCount, 'prompt group')} · ${m.textMode === 'SHARED' ? 'shared set' : 'unique per drop'} · ${m.assignment === 'RANDOM' ? 'random' : 'rotate'}${rankingWords(m)}`
      : 'no prompt groups yet';
  const options = [
    m.forceSend ? 'duplicates on' : 'duplicates off',
    `TLD ${m.shortLinkTldValue ?? 'auto'}`,
    m.aiOptimization ? 'AI on' : m.batches ? 'batches on' : 'no batches',
    shortlinkFormat(m),
  ]
    .filter(Boolean)
    .join(' · ');
  return [
    { key: 'gatewayStrategy', label: SECTION_LABEL.gatewayStrategy, value: (m.strategy && STRATEGY_LABELS[m.strategy]) || 'not set', on: Boolean(m.strategy) },
    { key: 'gatewayPool', label: SECTION_LABEL.gatewayPool, value: m.poolCount > 0 ? `${plural(m.poolCount, 'route')} · ${m.hasSenderGroup ? 'senders set' : 'no senders'}` : 'no routes yet', on: m.poolCount > 0 },
    { key: 'creative', label: SECTION_LABEL.creative, value: texts, on: m.textGroupCount > 0 },
    { key: 'options', label: SECTION_LABEL.options, value: options, on: true },
    { key: 'verdict', label: SECTION_LABEL.verdict, value: m.verdictEnabled ? `on${m.scalingEnabled ? ' · scaling on' : ''}` : 'off', on: m.verdictEnabled },
    { key: 'hourOptimizer', label: SECTION_LABEL.hourOptimizer, value: m.hourOptimizerOn ? `on · ${(m.lookbackPreset ?? '').replace(/-/g, ' ')} · +${m.maxExtraSlots ?? 0} max` : 'off', on: m.hourOptimizerOn },
    { key: 'amount', label: SECTION_LABEL.amount, value: `${m.defaultAmount.toLocaleString('en-US')} / drop`, on: true },
    { key: 'failMode', label: SECTION_LABEL.failMode, value: m.failMode === 'STOP_FOR_DAY' ? 'stop list' : 'complete list', on: true },
    { key: 'autoSend', label: SECTION_LABEL.autoSend, value: autoSendWords(m), on: m.autoSend.autoSend },
  ];
}

export const followersOf = (plan: DropGenPlanDetail, masterId: string): DropGenContainerSummary[] =>
  plan.containers.filter((c) => c.masterId === masterId);

/** Containers of the group's campaigns that still run on their own settings. */
export const notFollowing = (plan: DropGenPlanDetail, memberIds: ReadonlySet<number>): DropGenContainerSummary[] =>
  plan.containers.filter((c) => c.kind !== 'MASTER' && c.masterId === null && memberIds.has(c.campaignWiserId));

export interface OwnSetting {
  key: DropGenSection;
  /** "Default amount", as in the section list above. */
  label: string;
  /** "amount", for the one-line summary. */
  short: string;
  /** The container's own value when the summary carries it ("802"); null = its own, not shown. */
  value: string | null;
}

const SHORT: Record<DropGenSection, string> = {
  gatewayStrategy: 'strategy',
  gatewayPool: 'gateways',
  creative: 'texts',
  options: 'send options',
  verdict: 'verdicts',
  hourOptimizer: 'hours',
  amount: 'amount',
  failMode: 'mid-day fail',
  autoSend: 'auto-send',
};

function ownValue(c: DropGenContainerSummary, s: DropGenSection): string | null {
  if (s === 'amount') return c.defaultAmount.toLocaleString('en-US');
  if (s === 'failMode') return c.failMode === 'STOP_FOR_DAY' ? 'stop list' : 'complete list';
  return null;
}

/** What a follower keeps as its own instead of the master config's, in section order. */
export const ownSettings = (c: DropGenContainerSummary): OwnSetting[] =>
  DROP_GEN_SECTIONS.filter((s) => c.ownSections?.[s] === true).map((s) => ({
    key: s,
    label: SECTION_LABEL[s],
    short: SHORT[s],
    value: ownValue(c, s),
  }));

/** The one-line summary: the amount when it is own ("amount 802"), else the first own section; the rest count as "+N". */
export function ownSummary(own: OwnSetting[]): string {
  const amount = own.find((o) => o.key === 'amount' && o.value);
  return amount ? `amount ${amount.value}` : (own[0]?.short ?? '');
}
